import { Component, Input, OnChanges } from '@angular/core';
import { ContentService } from '@blk/cwp-ng-framework';
import { CommonChartUtil } from '@ddt-web/chart';
import { ColorsConfig, KeyCodeConstants, PortfolioType } from '@ddt-web/config';
import { InjectConfig, SharedConfigKey } from '@ddt-web/config-layer';
import { CellUtil, ComponentData, TabData, TabUtil } from '@ddt-web/data-service';
import { DcrUpdateUtil } from '@ddt-web/generic-utils';
import { ApplicationStateStoreProxyService } from '@ddt-web/shared-stores';
import * as dayjs from 'dayjs';
import * as customParseFormat from 'dayjs/plugin/customParseFormat';
import * as localeData from 'dayjs/plugin/localeData';
import * as objectSupport from 'dayjs/plugin/objectSupport';
import * as utc from 'dayjs/plugin/utc';
import * as get from 'lodash/get';
import * as merge from 'lodash/merge';
import * as range from 'lodash/range';
import { first } from 'rxjs/operators';

import { SeriesLabelIcon } from '../../enums/series-label-icon.enum';
import { HypotheticalGrowthValueService } from '../../services/hypothetical-growth-value.service';
import { PerformanceUtil } from '../../utils/performance.util';
import { GroupConfiguration } from '../models/group-configuration.interface';

dayjs.extend(customParseFormat);
dayjs.extend(localeData);
dayjs.extend(objectSupport);
dayjs.extend(utc);

@Component({
	selector: 'perf-hypothetical-performance-chart',
	templateUrl: './hypothetical-performance-chart.component.html',
	styleUrls: ['./hypothetical-performance-chart.component.scss']
})
export class HypotheticalPerformanceChartComponent implements OnChanges {
	private static MONTH_NAMES: string[] = dayjs.months();

	@InjectConfig(SharedConfigKey.ENABLE_HYPO_CHART_PERIOD_CHANGE) public readonly enableHypoChartPeriodChange: boolean = false;

	@Input() public data: ComponentData;
	@Input() public chartName: string;
	@Input() public displayCurrency = '$';
	@Input() public customChartAttributes: any = { growthMultiplier: 10000, displayCurrency: this.displayCurrency };
	@Input() public showSimplifiedDisclaimer: boolean = false;
	@Input() public customAdvisoryFee: number;
	@Input() public displayISOCurrency?: string;
	@Input() public tooltipText: string;
	@Input() public isDisclosureExpandable: boolean = false;
	@Input() public isCustomFeeEnabled: boolean = false;

	public DcrUpdateUtil = DcrUpdateUtil;

	public hasModel: boolean;
	public hasUserPortfolio: boolean;
	public cellUtils: CellUtil;
	public asOfDate: string;
	public hypotheticalAmountTxt: string = '10000';
	public hypotheticalAmountNum: number = 10000;
	public asOfDateMoment: dayjs.Dayjs;
	public earliestDataPoint: dayjs.Dayjs;
	public startMonthValue: string;
	public startMonthOptions: string[];
	public startYearValue: number;
	public startYearOptions: number[];
	public startTimestamp: number;
	public isInvalidInput: boolean = false;
	public isMonthUnselected: boolean = false;

	public seriesIcon = SeriesLabelIcon;
	public groupedLegendColours: Array<Array<string>>;
	public groupedLegendColumnConfiguration: Array<GroupConfiguration>;

	public partnerSeriesPrefixes: Array<string>;
	public partnerTooltipNameTemplate: string;
	public partnerTooltipPortfolioNameLength: number;

	public hypoGrossPerfChartTabData: TabData;
	public hypoCustomNetPerfChartTabData: TabData;
	public combinedPerfChartTabData: Array<TabData>;

	public isHypoGrossPerfValidData: boolean = false;
	public isHypoCustomNetPerfValidData: boolean = false;
	public isHypoGrossPerfChartValid: boolean = false;
	public isHypoCustomNetPerfChartValid: boolean = false;

	public shouldShowChart: boolean = false;

	constructor(
		private applicationStateStoreProxyService: ApplicationStateStoreProxyService,
		private contentService: ContentService,
		private hypotheticalGrowthValueService: HypotheticalGrowthValueService
	) { }

	public ngOnChanges(): void {
		this.applicationStateStoreProxyService
			.getPerformanceState()
			.pipe(first())
			.subscribe(performance => {
				this.hypotheticalAmountNum = performance?.hypotheticalGrowthMultiplier;
				this.hypotheticalAmountTxt = performance?.hypotheticalGrowthMultiplier?.toString();
				this.startTimestamp = performance?.hypotheticalGrowthStartTimestamp;
			});

		if (this.data.success) {
			// asOfDate is used for both gross and net hypothetical performance
			this.asOfDate = get(this.data.getTabById('hypo-perf-as-of-date'), 'tableData[0].cells[0][0].formattedValue');
			this.asOfDateMoment = dayjs.utc(this.asOfDate, 'DD/M/YYYY');

			this.partnerTooltipNameTemplate = this.contentService.get('HYPO_PERF_PARTNER_TOOLTIP_FORMAT', '<b>$prefix$</b> - $portfolio$');
			this.partnerTooltipPortfolioNameLength = Number(this.contentService.get('HYPO_PERF_PARTNER_TOOLTIP_NAME_LENGTH', '40'));
			this.partnerSeriesPrefixes = [
				this.contentService.get('HYPO_PERF_PARTNER_TOOLTIP_GROSS_PREFIX', 'Gross'),
				this.contentService.get('HYPO_PERF_PARTNER_TOOLTIP_CUSTOM_PREFIX', 'Net')
			];
			this.groupedLegendColumnConfiguration = [
				{
					heading: this.contentService.get('GROUPED_LEGEND_CATEGORY_1_HEADING', 'Gross Performance'),
					subHeading: this.contentService.get('GROUPED_LEGEND_CATEGORY_1_SUBHEADING', null)
				},
				{
					heading: DcrUpdateUtil.getAdvisoryFeeTextForPerformanceLegend(
						this.contentService,
						'GROUPED_LEGEND_CATEGORY_3_HEADING',
						'Net of $ADVISORY_FEE$% advisory fee',
						this.customAdvisoryFee
					),
					subHeading: this.contentService.get('GROUPED_LEGEND_CATEGORY_3_SUBHEADING', null)
				}
			];

			const hypoGrossPerfTableTabData = TabUtil.getVisibleTabData(this.data.getTabById('hypo-performance'));
			this.isHypoGrossPerfValidData = CellUtil.isValidCellData(hypoGrossPerfTableTabData);

			const hypoCustomNetPerfTableTabData = this.data.getTabById('hypo-net-custom-performance');
			this.isHypoCustomNetPerfValidData = this.isCustomFeeEnabled && !!this.customAdvisoryFee && CellUtil.isValidCellData(hypoCustomNetPerfTableTabData);

			// get the earliest data point if available
			const earliestDataPointDate = CellUtil.getById(this.data.getTabById('hypo-chart')?.tableData[0]?.cells[0], 'intervalStopDate')?.rawValue as string;
			this.earliestDataPoint = earliestDataPointDate ? dayjs.utc(earliestDataPointDate, 'YYYY-MM-DD') : null;

			// prepare chart data for gross and net
			const initialStartDate = this.startTimestamp ? dayjs(this.startTimestamp) : this.earliestDataPoint;
			this.hypoGrossPerfChartTabData = PerformanceUtil.getHypoChart(this.data.getTabById('hypo-chart'), initialStartDate);
			this.hypoCustomNetPerfChartTabData = this.isCustomFeeEnabled && this.customAdvisoryFee // check required when we exclude fee (store still has performance data)
				? PerformanceUtil.getHypoChart(this.data.getTabById('hypo-custom-net-chart'), initialStartDate) : null;

			// the ordering of these tabs is important!
			// each tab will pull colours from MULTI_SERIES_PORTFOLIO_COLORS based on the tab's index in this array
			// i.e. combinedPerfChartTabData[0] will pull colours from MULTI_SERIES_PORTFOLIO_COLORS[0]
			this.combinedPerfChartTabData = [this.hypoGrossPerfChartTabData, this.hypoCustomNetPerfChartTabData].filter(tab => tab && !tab.error);

			const groupedLegendColoursCopy = [...ColorsConfig.MULTI_SERIES_PORTFOLIO_COLORS];
			groupedLegendColoursCopy.pop();
			this.groupedLegendColours = groupedLegendColoursCopy;

			this.isHypoGrossPerfChartValid = !!this.hypoGrossPerfChartTabData && CellUtil.isValidCellData(this.hypoGrossPerfChartTabData);
			this.isHypoCustomNetPerfChartValid = !!this.hypoCustomNetPerfChartTabData && CellUtil.isValidCellData(this.hypoCustomNetPerfChartTabData);

			this.shouldShowChart = this.isCustomFeeEnabled && this.customAdvisoryFee
				? this.isHypoGrossPerfChartValid && this.isHypoCustomNetPerfChartValid
				: this.isHypoGrossPerfChartValid;

			this.setHasPortfolioTypeFlags();
		}

		if (this.enableHypoChartPeriodChange && this.earliestDataPoint) {
			this.setupStartDateSelections();
		}
		this.updateCustomChartAttributes();
	}

	private setupStartDateSelections(): void {
		this.startYearOptions = this.earliestDataPoint ? range(this.earliestDataPoint.year(), this.asOfDateMoment.year()) : [];
		this.startYearValue = this.startTimestamp ? dayjs(this.startTimestamp).year() : this.earliestDataPoint?.year();
		this.startMonthValue = this.startTimestamp ? dayjs(this.startTimestamp).format('MMMM') : this.earliestDataPoint?.add(1, 'month').format('MMMM');
		this.onStartYearChange();
	}

	private setHasPortfolioTypeFlags(): void {
		this.hasModel = false;
		this.hasUserPortfolio = false;
		this.data.portfolioData.forEach(portfolio => {
			if (portfolio && portfolio.field['portfolioType']) {
				if (portfolio.field['portfolioType'].rawValue === PortfolioType.PORTFOLIO) {
					this.hasUserPortfolio = true;
				}
				if (portfolio.field['portfolioType'].rawValue === PortfolioType.MODEL || portfolio.field['portfolioType'].rawValue === PortfolioType.THIRD_PARTY) {
					this.hasModel = true;
				}
			}
		});
	}

	/**
	 * @description onChangeHypoAmount: handles validation of the input (numbers and single decimal points).
	*/
	public onChangeHypoAmount(value: string): void {
		const output = this.hypotheticalGrowthValueService.validateHypoAmount(value);
		this.isInvalidInput = output.isInvalidInput;
		if (!output.isInvalidInput) {
			this.hypotheticalAmountNum = output.hypotheticalAmountNum;
			this.hypotheticalAmountTxt = output.hypotheticalAmountTxt;
		}
	}

	public onStartYearChange(): void {
		if (this.startYearValue && this.startYearValue === this.earliestDataPoint?.year()) {
			this.startMonthOptions = range(this.earliestDataPoint.month() + 1, 12);
		} else if (this.startYearValue && this.startYearValue === this.asOfDateMoment.year() - 1) {
			this.startMonthOptions = range(this.asOfDateMoment.month() + 1);
		} else {
			this.startMonthOptions = range(12);
		}
		this.startMonthOptions = this.startMonthOptions.map(i => HypotheticalPerformanceChartComponent.MONTH_NAMES[i]);

		if (!this.startMonthOptions.includes(this.startMonthValue)) {
			this.startMonthValue = undefined;
			this.isMonthUnselected = true;
		}
	}

	/**
	 * @description onKeyUp: updates hypothetical growth on ENTER.
	*/
	public onKeyUp(event): boolean {
		if (event.keyCode === KeyCodeConstants.ENTER && !this.isInvalidInput) {
			this.updateHypotheticalGrowth();
			return true;
		}
		return false;
	}

	/**
	 * @description updateHypotheticalGrowth: updates the multiplier value and the start timestamp for the hypothetical grow chart data.
	*/
	public updateHypotheticalGrowth(): void {
		if (!this.isInvalidInput && !this.isMonthUnselected) {
			if (this.enableHypoChartPeriodChange && this.earliestDataPoint) {
				this.startTimestamp = dayjs({
					year: this.startYearValue,
					month: HypotheticalPerformanceChartComponent.MONTH_NAMES.indexOf(this.startMonthValue)
				}).endOf('month').subtract(1, 'month').startOf('day').valueOf(); // we need to send the end of previous month as starting chart point
				this.hypotheticalGrowthValueService.updateHypotheticalGrowthStart(this.startTimestamp);
				this.hypoGrossPerfChartTabData = HypotheticalGrowthValueService.prepareAccumulativeReturns(this.data.getTabById('hypo-chart'), dayjs.utc(this.startTimestamp));
				this.combinedPerfChartTabData = [this.hypoGrossPerfChartTabData].filter(tab => tab && !tab.error);
			}
			this.updateCustomChartAttributes();
			this.hypotheticalGrowthValueService.updateHypotheticalGrowth(this.hypotheticalAmountNum);
		}
	}

	private updateCustomChartAttributes(): void {
		const newAttributes = { ...this.customChartAttributes };
		newAttributes['growthMultiplier'] = this.hypotheticalAmountNum;
		newAttributes['displayCurrency'] = this.displayCurrency;

		if (this.isHypoGrossPerfChartValid && this.isCustomFeeEnabled) {
			merge(newAttributes, { legend: { enabled: false } });
			merge(newAttributes, { chart: { marginBottom: 60 } });
			newAttributes['multiSeriesConfig'] = CommonChartUtil.createCustomisationsArray(
				this.groupedLegendColours,
				this.partnerSeriesPrefixes,
				this.partnerTooltipNameTemplate,
				this.partnerTooltipPortfolioNameLength
			);
		}

		if (get(this.customChartAttributes, 'yAxis.plotLines')) {
			newAttributes['yAxis']['plotLines'][0].value = this.hypotheticalAmountNum;
		}

		this.customChartAttributes = newAttributes;
	}
}
